import { useMutation, useQueryClient } from '@tanstack/react-query';
import { IUserDetailsTabsStats } from './useRequestGetUserDetailsTabsStats';
import { handleApiResponse } from '@/functions';
import { useInvalidateQueries } from '@/hooks';
import { apiClient } from '@/components/api';
import routes from '@/lib/routes';

type TParams = {
  disableInvalidateQueries?: boolean;
};

type TDataVariables = {
  userId: string;
  position: string
};
type TResponseData = void;

// Helper to build the stats query key (must match useRequestGetUserDetailsTabsStats)
const getStatsQueryKey = (userId: string) => [routes.api.users.base, 'tabs-stats', userId, true];

export const useRequestDeleteUserImage = (params?: TParams) => {

  const { invalidate } = useInvalidateQueries();
  const queryClient = useQueryClient();
  const { isPending, error, mutateAsync } = useMutation({
    mutationKey: [routes.api.deleteProfilePicture],
    mutationFn: (data: TDataVariables) =>
      apiClient
        .post<TResponseData>(routes.api.deleteProfilePicture, data)
        .then(handleApiResponse),
    
    // Optimistic update: decrement photosCount immediately
    onMutate: async (variables) => {
      const queryKey = getStatsQueryKey(variables.userId);
      
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey });
      
      // Snapshot the previous value for rollback
      const previousStats = queryClient.getQueryData<IUserDetailsTabsStats>(queryKey);
      
      // Optimistically decrement the photo count
      if (previousStats) {
        queryClient.setQueryData<IUserDetailsTabsStats>(queryKey, {
          ...previousStats,
          photosCount: Math.max(0, previousStats.photosCount - 1)
        });
      }
      
      // Return context with the snapshot for rollback
      return { previousStats, queryKey };
    },
    
    // Rollback on error
    onError: (_err, _variables, context) => {
      if (context?.previousStats) {
        queryClient.setQueryData(context.queryKey, context.previousStats);
      }
    },
    
    // On success, only invalidate verification queries (not stats - we already updated optimistically)
    onSuccess: () => {
      if (params?.disableInvalidateQueries) return;
      
      // Invalidate verification queries only
      Promise.all([
        invalidate(routes.api.verifications.count),
        invalidate(routes.api.verifications.photo)
      ]);
    }
  });

  const triggerDeleteUserImage = (data: TDataVariables) =>
    mutateAsync(data);

  return ({
    isLoading: isPending,
    triggerDeleteUserImage,
    error
  });

};  
